import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, throwError, from, of } from 'rxjs';
import { timeout, tap, catchError, map, switchMap } from 'rxjs/operators';
import { CacheService } from '../../../shared/services/cache/cache.service';
import { gigyaInfo } from '../../../types/gigyaInfo';
import { environment } from '../../../../environments/environment.dev';
import { TokenService } from '../../../shared/services/token/token.service';
import { LoginService } from '../../../features/login/login.service';
import { JweCredentialsEncryption } from '../../utils/jwe-credentials-encryption.util';

/**
 * AuthService is responsible for managing user authentication and session state.
 * It provides methods for login, registration and everything related to user authentication.
 * It also handles the Gigya API for user management.
 */

// the gigya variable is used to access the Gigya API. It is defined in the global scope by the Gigya script loaded by the appComponent
declare var gigya: any;

// TFA Challenge interface
export interface TfaChallenge {
  requiresTfa: true;
  gigyaAssertion: string;
  phvToken: string;
  regToken: string;
  email: string;
  password: string;
}

@Injectable({
  providedIn: 'root'
})

export class AuthService {

  loggedInUser: gigyaInfo | null = null;
  accessToken = '';
  gigyaToken = '';

  private gigyaUserSubject: BehaviorSubject<gigyaInfo | null>;
  private GIGYA_USER_KEY = 'gigyaUser';
  private sb_url = environment.do_services_sb_url;
  
  // this is used to store the Gigya user details
  gigyaUser$: Observable<gigyaInfo | null>;

  constructor(private ngZone: NgZone, private cacheService: CacheService, private loginService: LoginService,
              private tokenService: TokenService, private httpClient: HttpClient) {

    // Initialize the BehaviorSubject with null
    // This will be used to emit the Gigya user details to subscribers
    this.gigyaUserSubject = new BehaviorSubject<gigyaInfo | null>(null);
    this.gigyaUser$ = this.gigyaUserSubject.asObservable();

    // Restore token from sessionStorage (for payment redirect flow)
    const storedToken = sessionStorage.getItem('currentToken');
    if(storedToken) {
      this.accessToken = storedToken;
      this.loginService.accessToken.next(storedToken);
    }

    const token = this.loginService.gigyaResponse;
    
    // if the token is not null, decode it and set the loggedInUser
    // this is used to restore the user session when the app is reloaded
    if(token) {
      const loggedInUserDetails = token;
      this.setLoggedInUser(loggedInUserDetails);
    }
  }

  setLoggedInUser(user: gigyaInfo | null) {
    this.gigyaUserSubject.next(user);
    this.loggedInUser = user;
  }

  isOrgAdmin(userDetails: any): boolean {
    return userDetails?.accessIdentifier === 'ORGADMIN' ||
      (userDetails?.accessIdentifier === 'ORGUSER' && userDetails?.accountId === 6 && userDetails?.coAdmin === true);
  }

  login() {
    this.loggedInUser = {} as gigyaInfo; // this is just a placeholder
  }

 
  gigyaLogin(loginId: string, password: string): Observable<any> {
    return new Observable(observer => {
      gigya.accounts.login({
        apiKey: environment.api_key,
        loginID: loginId,
        password: password,
        callback: (response: any) => {
          // Error codes 403101 and 403102 mean TFA is required - proceed with TFA flow
          if(response.errorCode && response.errorCode !== 0 && response.errorCode !== 403101 && response.errorCode !== 403102) {
            observer.error(response);
          } else {
            observer.next(response);
          }
          observer.complete();
        }
      });
    });
  }

 
  gigyaInitTfa(regToken: string, provider: string = 'email', mode: string = 'verify'): Observable<any> {
    return new Observable(observer => {
      
      gigya.accounts.tfa.initTFA({
       apiKey: environment.api_key,
        regToken: regToken,
        provider: 'gigyaEmail',
        mode: mode,
        callback: (response: any) => {
          if(response.errorCode && response.errorCode !== 0) {
            observer.error(response);
          } else {
            observer.next(response);
          }
          observer.complete();
        }
      });
    });
  }

 
  gigyaGetEmails(gigyaAssertion: string): Observable<any> {
    return new Observable(observer => {
      
      gigya.accounts.tfa.email.getEmails({
        apiKey: environment.api_key,
        gigyaAssertion: gigyaAssertion,
        callback: (response: any) => {
          if(response.errorCode && response.errorCode !== 0) {
            observer.error(response);
          } else {
            observer.next(response);
          }
          observer.complete();
        }
      });
    });
  }

  /**
   * Step 4: Send email verification code
   * @param gigyaAssertion - Assertion from initTFA response
   * @param emailID - Email ID from getEmails response
   * @returns Observable with send verification code response
   */
  gigyaSendEmailVerificationCode(gigyaAssertion: string, emailID: string): Observable<any> {
    return new Observable(observer => {
      
      gigya.accounts.tfa.email.sendVerificationCode({
        apiKey: environment.api_key,
        gigyaAssertion: gigyaAssertion,
        emailID: emailID,
        callback: (response: any) => {
          if(response.errorCode && response.errorCode !== 0) {
            observer.error(response);
          } else {
            observer.next(response);
          }
          observer.complete();
        }
      });
    });
  }

  
  gigyaCompleteEmailVerification(gigyaAssertion: string, phvToken: string, code: string): Observable<any> {
    return new Observable(observer => {
      
      gigya.accounts.tfa.email.completeVerification({
        apiKey: environment.api_key,
        gigyaAssertion: gigyaAssertion,
        phvToken: phvToken,
        code: code,
        callback: (response: any) => {
          if(response.errorCode && response.errorCode !== 0) {
            observer.error(response);
          } else {
            observer.next(response);
          }
          observer.complete();
        }
      });
    });
  }

  
  gigyaFinalizeTfa(gigyaAssertion: string, regToken: string, providerAssertion: string): Observable<any> {
    return new Observable(observer => {
      
      gigya.accounts.tfa.finalizeTFA({
        apiKey: environment.api_key,
        gigyaAssertion: gigyaAssertion,
        regToken: regToken,
        providerAssertion: providerAssertion,
        callback: (response: any) => {
          if(response.errorCode && response.errorCode !== 0) {
            observer.error(response);
          } else {
            observer.next(response);
          }
          observer.complete();
        }
      });
    });
  }

  actualLogin(userInfo: { email: string, password: string }): Observable<gigyaInfo | TfaChallenge | null> {

    
    return this.gigyaLogin(userInfo.email, userInfo.password).pipe(
      
      switchMap((loginResponse: any) => {
        // Error codes 403101 or 403102 mean "Account Pending TFA" - proceed with TFA
        // Any other error code should throw
        if(loginResponse.errorCode && loginResponse.errorCode !== 0 && loginResponse.errorCode !== 403101 && loginResponse.errorCode !== 403102) {
          throw loginResponse;
        }
        
        // If login successful (errorCode: 0) and profile exists, TFA was already completed
        // Skip TFA flow and go directly to backend authentication
        if(loginResponse.errorCode === 0 && loginResponse.profile) {
          console.log('✅ Login successful - TFA already completed, calling backend directly');
          return this.authenticateWithBackend(userInfo.email, userInfo.password);
        }
        
        // Check if we have a regToken (needed for TFA)
        if(!loginResponse.regToken) {
          throw new Error('No regToken found in Gigya login response');
        }
        
        
        // Step 2: Call accounts.tfa.initTFA with the regToken
        const regToken = loginResponse.regToken;
        return this.gigyaInitTfa(regToken, 'gigyaEmail', 'verify').pipe(
          
          switchMap((tfaResponse: any) => {
            // Step 3: Get available emails for TFA
            const gigyaAssertion = tfaResponse.gigyaAssertion;
            return this.gigyaGetEmails(gigyaAssertion).pipe(
              
              switchMap((emailsResponse: any) => {
                // Step 4: Send email verification code using email ID from getEmails response
                const emailID = emailsResponse.emails && emailsResponse.emails.length > 0 
                  ? emailsResponse.emails[0].id 
                  : null;
                
                if(!emailID) {
                  throw new Error('No email ID found in getEmails response');
                }
                
                return this.gigyaSendEmailVerificationCode(gigyaAssertion, emailID).pipe(map(sendCodeResponse => {
                  // Return TFA challenge for OTP entry
                  const tfaChallenge: TfaChallenge = {
                      requiresTfa: true,
                      gigyaAssertion: tfaResponse.gigyaAssertion,
                      phvToken: sendCodeResponse.phvToken,
                      regToken: loginResponse.regToken,
                      email: userInfo.email,
                      password: userInfo.password
                    };
                    return tfaChallenge;
                  })
                );
              })
            );
          })
        );
      }),
      catchError((error: any) => {
        const errorResponse = {
          orgAdminError: true,
          ...error
        };
        return throwError(() => errorResponse);
      })
    );
  }

  
  completeTfaLogin(tfaChallenge: TfaChallenge, otpCode: string): Observable<gigyaInfo | null> {
   
    
    return this.gigyaCompleteEmailVerification(
      tfaChallenge.gigyaAssertion,
      tfaChallenge.phvToken,
      otpCode
    ).pipe(
      switchMap((verificationResponse: any) => {
        // Step 7: Finalize TFA with providerAssertion
        const providerAssertion = verificationResponse.providerAssertion;
        
        if(!providerAssertion) {
          throw new Error('No providerAssertion found in verification response');
        }
        
        console.log('Calling finalizeTFA with providerAssertion:', providerAssertion);
        
        return this.gigyaFinalizeTfa(
          tfaChallenge.gigyaAssertion,
          tfaChallenge.regToken,
          providerAssertion
        ).pipe(
          tap((finalizeResponse: any) => {
            console.log('TFA finalized:', finalizeResponse);
          }),
          map(finalizeResponse => ({ verificationResponse, finalizeResponse }))
        );
      }),
      switchMap(({ verificationResponse, finalizeResponse }) => {
        // Step 8: Authenticate with backend after TFA completion
        console.log('TFA complete - authenticating with backend');
        return this.authenticateWithBackend(tfaChallenge.email, tfaChallenge.password);
      })
    );
  }

  // this method is used to handle the response from the Gigya API after login.
  private handleGigyaLoginResponse(response: any) {
    if(!response || response.errorCode === 206002 || response.status === 'FAIL') {
      this.ngZone.run(
        () => {
          alert("error");
          if(!environment.production) {
            console.log(response);
          }

        }
      )
    } else {
      if(!environment.production) {
        console.log(response);
      }
      gigya.accounts.getAccountInfo({
        include: "loginIDs,emails,profile,data",
        // callback: (accObj: any) => this.getAccountInfo(accObj)
      })
    }
  }

  logout(): Observable<void> {
    if(!gigya || this.gigyaUserSubject.value == null) {
      console.error('Gigya is not initialized or user is not logged in');
      return throwError(() => 'Gigya is not initialized or user is not logged in');
    }

    const logoutUrl = `${this.sb_url}gigya/userLogout`;

    return this.httpClient.post<void>(logoutUrl, {}, {
      headers: { 'Content-Type': 'application/json' }
    }).pipe(
      timeout(3000), // Don't wait more than 3 seconds
      tap(() => {
        // Clear on success
        this.cacheService.clearItem(this.GIGYA_USER_KEY);
        this.cacheService.clearAll();
        this.setLoggedInUser(null);
        this.accessToken = '';
        this.gigyaToken = '';
        this.loginService.accessToken.next('');
        this.loginService.gigyaToken.next('');
        this.loginService.clearGigyaResponse();
      }),
      catchError((error: any) => {
        // Clear even on error - user should be logged out locally
        console.error('Logout error, clearing local state anyway:', error);
        this.cacheService.clearItem(this.GIGYA_USER_KEY);
        this.cacheService.clearAll();
        this.setLoggedInUser(null);
        this.accessToken = '';
        this.gigyaToken = '';
        this.loginService.accessToken.next('');
        this.loginService.gigyaToken.next('');
        this.loginService.clearGigyaResponse();
        return of(void 0); // Return success anyway
      }),
      map(() => void 0)
    );
  }

  isAuthenticated() {
    return this.loggedInUser != null && Object.keys(this.loggedInUser).length > 0;
  }

  /**
   * Private method to authenticate user with backend after Gigya validation
   * Encrypts credentials, calls backend API, and sets up user session
   * @param email User email
   * @param password User password
   * @returns Observable with user details
   */
  private authenticateWithBackend(email: string, password: string): Observable<gigyaInfo> {
    return from(JweCredentialsEncryption.encrypt(
      email,
      password,
      environment.jwe_secret
    )).pipe(
      switchMap((secureBlob: string) => {
        console.log('Sending to backend /gigya/accountsInfo with encrypted credentials');
        return this.httpClient.post<any>(`${this.sb_url}gigya/accountsInfo`, {
          secureBlob: secureBlob
        }, { responseType: 'text' as 'json' });
      }),
      map((resp: any) => {
        if(resp) {
          const decodedToken = this.tokenService.decodeJwtToken(resp);
          
          if(decodedToken.userContext.error) {
            const userContext = decodedToken.userContext;
            userContext.orgAdminError = true;
            throw userContext;
          } 
          
          const userDetails = decodedToken.userContext;
          this.accessToken = decodedToken.token;
          this.gigyaToken = decodedToken;

          if(this.isOrgAdmin(userDetails)) {
            this.setLoggedInUser(userDetails);
            this.cacheService.setApiKey(environment.api_key_orgAdmin); 
            this.loginService.accessToken.next(this.accessToken);
            this.loginService.gigyaToken.next(this.gigyaToken);
          } else if(userDetails.accessIdentifier === 'INDADMIN') {
            this.setLoggedInUser(userDetails);
            this.cacheService.setApiKey(environment.api_key_single);
            this.loginService.accessToken.next(this.accessToken);
            this.loginService.gigyaToken.next(this.gigyaToken);
          } else {
            // User is not authorized — do not create a session
            this.cacheService.setApiKey(environment.api_key_orgUser);
            this.cacheService.clearAll();
          }
          return userDetails;
        } else {
          const errorResponse = {
            error: 'No response from backend',
            orgAdminError: true
          };
          throw errorResponse;
        }
      }),
      catchError((error: any) => {
        console.error('Error calling backend:', error);
        const errorResponse = {
          orgAdminError: true,
          ...error
        };
        return throwError(() => errorResponse);
      })
    );
  }

}